import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { SELECTORS } from '../../constants/selectors';

/**
 * 都道府県コンテナの非表示に使うクラス。
 *
 * base.css の `.hidden` は `display:none !important` のため、
 * `Shopify.CountryProvinceSelector` が行う `container.style.display = ''`（インライン解除）では
 * 打ち消せず、都道府県セレクトが恒久的に非表示になる不具合があった。
 * そのため Liquid 側は `tw:hidden` を使い、開閉は本コンポーネントが classList で行う。
 */
const PROVINCE_HIDDEN_CLASS = 'tw:hidden';

/**
 * 住所フォーム開閉コンポーネント
 *
 * @element kd-address-form-toggle
 * @description 住所の追加・編集フォームの開閉を aria-expanded で制御する。
 *              キャンセルボタンでフォームを閉じる。
 *              Shopify.CountryProvinceSelector の初期化タイミングと、
 *              都道府県コンテナの表示同期も担う。
 */
@customElement('kd-address-form-toggle')
export class KdAddressFormToggle extends LitElement {
  createRenderRoot() {
    return this;
  }

  /** 国セレクトに登録した都道府県同期ハンドラ（disconnectedCallback で解除するため保持） */
  private provinceSyncBindings: { el: HTMLElement; handler: () => void }[] = [];

  private handleToggleClick = (e: Event): void => {
    const target = e.currentTarget as HTMLElement;
    this.toggleExpanded(target);
  };

  private handleCancelClick = (e: Event): void => {
    const target = e.currentTarget as HTMLElement;
    const container = target.closest(SELECTORS.ACCOUNT.ADDRESS_CONTAINER);
    if (!container) return;

    const expandedBtn = container.querySelector(`[${SELECTORS.ACCOUNT.ADDRESS_EXPANDED_ATTR}]`) as HTMLElement | null;
    if (expandedBtn) {
      this.toggleExpanded(expandedBtn);
    }
  };

  connectedCallback(): void {
    super.connectedCallback();

    // 順序が重要: CountryProvinceSelector が都道府県 option を投入した後に表示同期する
    this.setupCountrySelectors();
    this.setupProvinceVisibility();

    const toggleButtons = this.querySelectorAll(SELECTORS.ACCOUNT.ADDRESS_TOGGLE_BUTTON);
    toggleButtons.forEach((btn) => btn.addEventListener('click', this.handleToggleClick));

    const cancelButtons = this.querySelectorAll(SELECTORS.ACCOUNT.ADDRESS_CANCEL_BUTTON);
    cancelButtons.forEach((btn) => btn.addEventListener('click', this.handleCancelClick));
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();

    const toggleButtons = this.querySelectorAll(SELECTORS.ACCOUNT.ADDRESS_TOGGLE_BUTTON);
    toggleButtons.forEach((btn) => btn.removeEventListener('click', this.handleToggleClick));

    const cancelButtons = this.querySelectorAll(SELECTORS.ACCOUNT.ADDRESS_CANCEL_BUTTON);
    cancelButtons.forEach((btn) => btn.removeEventListener('click', this.handleCancelClick));

    this.provinceSyncBindings.forEach(({ el, handler }) => el.removeEventListener('change', handler));
    this.provinceSyncBindings = [];
  }

  private toggleExpanded(target: HTMLElement): void {
    const current = target.getAttribute('aria-expanded');
    target.setAttribute('aria-expanded', (current === 'false').toString());
  }

  private setupCountrySelectors(): void {
    const Shopify = (window as unknown as Record<string, unknown>).Shopify as
      | { CountryProvinceSelector?: new (country: string, province: string, options: { hideElement: string }) => void }
      | undefined;

    if (!Shopify?.CountryProvinceSelector) return;

    // 新規住所フォームの国・都道府県セレクタ
    // eslint-disable-next-line no-new
    try { new Shopify.CountryProvinceSelector('AddressCountryNew', 'AddressProvinceNew', {
      hideElement: 'AddressProvinceContainerNew',
    }); } catch { /* 要素が存在しない場合はスキップ */ }

    // 編集フォームの国・都道府県セレクタ
    const countrySelects = this.querySelectorAll(SELECTORS.ACCOUNT.ADDRESS_COUNTRY_SELECT);
    countrySelects.forEach((select) => {
      const formId = (select as HTMLElement).dataset.formId;
      if (!formId) return;

      // eslint-disable-next-line no-new
      try { new Shopify.CountryProvinceSelector!(`AddressCountry_${formId}`, `AddressProvince_${formId}`, {
        hideElement: `AddressProvinceContainer_${formId}`,
      }); } catch { /* 要素が存在しない場合はスキップ */ }
    });
  }

  /**
   * 都道府県コンテナの表示同期をすべてのフォームに仕掛ける。
   *
   * 判定材料は「都道府県 select の option 数」だけなので、
   * Shopify.CountryProvinceSelector が読み込まれていなくても成立する
   * （＝ setupCountrySelectors の early return に巻き込まれない）。
   */
  private setupProvinceVisibility(): void {
    this.bindProvinceSync('AddressCountryNew', 'AddressProvinceNew', 'AddressProvinceContainerNew');

    const countrySelects = this.querySelectorAll(SELECTORS.ACCOUNT.ADDRESS_COUNTRY_SELECT);
    countrySelects.forEach((select) => {
      const formId = (select as HTMLElement).dataset.formId;
      if (!formId) return;

      this.bindProvinceSync(
        `AddressCountry_${formId}`,
        `AddressProvince_${formId}`,
        `AddressProvinceContainer_${formId}`,
      );
    });
  }

  /**
   * 都道府県コンテナの表示を国セレクトの状態に同期させる。
   *
   * `Shopify.CountryProvinceSelector` はコンストラクタ内で change リスナを登録した後に
   * `initCountry()` で都道府県 option を投入する（assets/global.js:235-238）。
   * 本メソッドはその後に呼ばれるため、初回同期時点で option 数が確定している。
   * change リスナも後から登録するので、ライブラリの再投入後に実行される。
   */
  private bindProvinceSync(countryId: string, provinceId: string, containerId: string): void {
    const countryEl = document.getElementById(countryId);
    if (!countryEl) return;

    const handler = (): void => this.syncProvinceVisibility(containerId, provinceId);
    handler();
    countryEl.addEventListener('change', handler);
    this.provinceSyncBindings.push({ el: countryEl, handler });
  }

  /** 都道府県 option が無い国（＝都道府県を持たない国）ではコンテナを隠す */
  private syncProvinceVisibility(containerId: string, provinceId: string): void {
    const container = document.getElementById(containerId);
    const provinceEl = document.getElementById(provinceId) as HTMLSelectElement | null;
    if (!container || !provinceEl) return;

    container.classList.toggle(PROVINCE_HIDDEN_CLASS, provinceEl.options.length === 0);
  }

  render() {
    return nothing;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-address-form-toggle': KdAddressFormToggle;
  }
}
